// Proper country code to flag emoji converter with fallback
export const getCountryFlag = (countryCode) => {
  if (!countryCode || typeof countryCode !== 'string') {
    return '🌐'; // Globe emoji as fallback
  }
  
  const code = countryCode.toUpperCase().trim();
  
  // If it's already formatted with flag emoji prefix (like "🇪🇸 Spain"), extract just the code
  const cleanCode = code.replace(/[^\w]/g, '').substring(0, 2);
  
  if (cleanCode.length !== 2) {
    return '🌐';
  }
  
  // Convert country code to flag emoji
  // Each letter becomes its regional indicator symbol
  // A=🇦, B=🇧, etc. (Unicode: 0x1F1E6 = A)
  const codePoints = [...cleanCode].map(char => 
    0x1F1E6 - 65 + char.charCodeAt(0)
  );
  
  return String.fromCodePoint(...codePoints);
};

// Alternative: Return flag with country code fallback for systems without emoji support
export const getCountryFlagWithFallback = (countryCode) => {
  if (!countryCode || typeof countryCode !== 'string') {
    return <span title="Unknown country">🌐</span>;
  }
  
  const code = countryCode.toUpperCase().trim().substring(0, 2);
  const flag = getCountryFlag(code);
  
  // Return both emoji and code for better cross-platform support
  return (
    <span 
      title={code}
      style={{ fontFamily: '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif' }}
    >
      {flag}
    </span>
  );
};

// Utility to get user initials from name
export const getUserInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Utility to generate color for user avatar based on name
export const getAvatarColor = (name) => {
  if (!name) return 'bg-gray-500';
  
  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-indigo-500',
    'bg-red-500',
    'bg-yellow-500',
    'bg-teal-500',
  ];
  
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

// Get user image or fallback to initials
export const getUserAvatar = (user) => {
  if (!user) {
    return {
      type: 'initials',
      initials: '?',
      color: 'bg-gray-500'
    };
  }

  // Check for avatar_url from JWT token (primary method)
  if (user.avatar_url) {
    return {
      type: 'image',
      src: user.avatar_url,
      initials: getUserInitials(user.name || user.identifier),
      color: getAvatarColor(user.name || user.identifier)
    };
  }
  
  // Check for Knack field_115_raw format (fallback for direct Knack data)
  if (user.field_115_raw?.url) {
    return {
      type: 'image',
      src: user.field_115_raw.url,
      initials: getUserInitials(user.name || user.identifier),
      color: getAvatarColor(user.name || user.identifier)
    };
  }
  
  // Fallback to initials
  return {
    type: 'initials',
    initials: getUserInitials(user.name || user.identifier),
    color: getAvatarColor(user.name || user.identifier)
  };
};
